/**
 * =============================================================================
 *  DOC VA GHI LUONG NOI DUNG CUA MOT TRANG - qua pdf-lib, o muc thap
 * =============================================================================
 *
 *  pdf-lib coi than cua mot luong la byte mo duc. No giai nen duoc (moi bo loc
 *  pho bien deu co trong decodePDFRawStream) va no nen lai duoc (flateStream),
 *  nhung giua hai buoc do no khong hieu gi ca. Phan hieu la viec cua tep
 *  imageBlocks.ts; tep nay chi lo dua byte ra vao cho dung.
 *
 * =============================================================================
 *  /Contents CO THE LA MOT MANG - va thuong la
 * =============================================================================
 *  Dac ta cho phep /Contents la mot mang luong. Khi do cac luong duoc NOI LAI
 *  voi nhau (co ky tu trang xen giua) va duoc doc nhu MOT luong duy nhat. Mot
 *  toan tu duoc phep bat dau o luong nay va ket thuc o luong sau.
 *
 *  He qua cho tep nay: khong duoc xu ly tung luong rieng le. Ta noi tat ca lai,
 *  sua tren chuoi da noi, roi ghi tra ve DUNG MOT luong. Ghi tra thanh mot luong
 *  la dung dac ta va no lam bien mat ca lop cai bay "toan tu bi cat doi".
 *
 * =============================================================================
 *  NGUYEN TAC: KHONG SUA DUOC THI KHONG SUA GI
 * =============================================================================
 *  Bo loc la (JPXDecode tren mot content stream, mot bo loc rieng cua nha san
 *  xuat) thi ta TU CHOI, khong doan. Mot luong giai nen sai cho ra byte rac, va
 *  ghi byte rac vao /Contents la xoa trang cua nguoi dung.
 */
import {
  PDFArray,
  PDFDict,
  PDFName,
  PDFRawStream,
  PDFRef,
  PDFStream,
  decodePDFRawStream,
  type PDFDocument,
} from 'pdf-lib';

export interface PageContentHandle {
  /** Toan bo luong noi dung cua trang, da giai nen va noi lai. */
  bytes: Uint8Array;
  /**
   * Ghi tra mot luong noi dung MOI.
   *
   * Ghi de len object cua luong DAU TIEN va bo cac luong con lai khoi /Contents.
   * Dung lai object cu thay vi tao object moi de khong de lai object mo coi
   * trong tep sau moi lan keo anh.
   */
  write(next: Uint8Array): void;
}

export type PageContentResult =
  | { ok: true; value: PageContentHandle }
  | { ok: false; reason: string };

/** Doc luong noi dung cua mot trang. */
export function readPageContent(doc: PDFDocument, pageIndex: number): PageContentResult {
  const pages = doc.getPages();
  const page = pages[pageIndex];
  if (!page) {
    return { ok: false, reason: `Trang ${pageIndex + 1} không tồn tại trong tài liệu này.` };
  }

  const contents = page.node.Contents();
  if (!contents) {
    return {
      ok: false,
      reason: `Trang ${pageIndex + 1} không có luồng nội dung nào, nên không có gì để sửa.`,
    };
  }

  const refs: PDFRef[] = [];
  const parts: Uint8Array[] = [];

  const collect = (entry: unknown, ref: PDFRef | null): string | null => {
    const stream = entry instanceof PDFStream ? entry : null;
    if (!stream) return 'Một phần tử trong /Contents của trang không phải luồng nội dung.';
    const decoded = decodeStream(stream);
    if (typeof decoded === 'string') return decoded;
    parts.push(decoded);
    if (ref) refs.push(ref);
    return null;
  };

  if (contents instanceof PDFArray) {
    for (let i = 0; i < contents.size(); i++) {
      const ref = contents.get(i);
      const resolved = doc.context.lookup(ref);
      const failure = collect(resolved, ref instanceof PDFRef ? ref : null);
      if (failure) return { ok: false, reason: failure };
    }
  } else {
    const ref = doc.context.getObjectRef(contents) ?? null;
    const failure = collect(contents, ref);
    if (failure) return { ok: false, reason: failure };
  }

  if (parts.length === 0) {
    return { ok: false, reason: 'Không đọc được luồng nội dung nào của trang.' };
  }
  if (refs.length === 0) {
    return {
      ok: false,
      reason:
        'Luồng nội dung của trang không nằm trong một object gián tiếp nên không ghi lại được. ' +
        'Cần lõi Rust/PDFium.',
    };
  }

  // NOI BANG KY TU XUONG DONG, khong noi khit. Toan tu cuoi cua luong truoc va
  // toan tu dau cua luong sau se dinh lien thanh mot tu vo nghia neu noi khit.
  const bytes = joinWithNewline(parts);

  const write = (next: Uint8Array): void => {
    const firstRef = refs[0] as PDFRef;
    doc.context.assign(firstRef, doc.context.flateStream(next));
    if (contents instanceof PDFArray) {
      // Giu DUNG mot phan tu. Cac object cu con lai khong bi xoa khoi tep - de
      // xoa an toan can biet chac khong ai khac tro toi chung - nhung chung
      // khong con nam trong /Contents nen khong con anh huong toi trang.
      while (contents.size() > 1) contents.remove(contents.size() - 1);
      contents.set(0, firstRef);
    }
  };

  return { ok: true, value: { bytes, write } };
}

/** Giai nen mot luong. Tra ve chuoi = ly do tu choi. */
function decodeStream(stream: PDFStream): Uint8Array | string {
  if (!(stream instanceof PDFRawStream)) {
    // Luong do chinh pdf-lib dung trong bo nho (PDFContentStream) - chua nen.
    try {
      return stream.getContents();
    } catch {
      return 'Không đọc được nội dung của một luồng trong /Contents.';
    }
  }

  const filter = stream.dict.get(PDFName.of('Filter'));
  const names = filterNames(filter);
  const unsupported = names.filter((n) => !DECODABLE.has(n));
  if (unsupported.length > 0) {
    return (
      `Luồng nội dung của trang dùng bộ lọc ${unsupported.join(', ')}, mà bản web không giải nén ` +
      'được. Ghi đè lên một luồng chưa giải nén đúng sẽ xóa trắng trang, nên thao tác bị từ chối.'
    );
  }

  try {
    return decodePDFRawStream(stream).decode();
  } catch (err) {
    return (
      'Không giải nén được luồng nội dung của trang ' +
      `(${String(err).slice(0, 100)}). Thao tác bị từ chối để không làm hỏng trang.`
    );
  }
}

/** Bo loc ma decodePDFRawStream cua pdf-lib xu ly duoc. */
const DECODABLE = new Set([
  'FlateDecode',
  'LZWDecode',
  'ASCII85Decode',
  'ASCIIHexDecode',
  'RunLengthDecode',
]);

function filterNames(filter: unknown): string[] {
  if (!filter) return [];
  if (filter instanceof PDFName) return [filter.asString().replace(/^\//, '')];
  if (filter instanceof PDFArray) {
    const out: string[] = [];
    for (let i = 0; i < filter.size(); i++) {
      const item = filter.get(i);
      if (item instanceof PDFName) out.push(item.asString().replace(/^\//, ''));
      else out.push('không-rõ');
    }
    return out;
  }
  return ['không-rõ'];
}

function joinWithNewline(parts: readonly Uint8Array[]): Uint8Array {
  let size = 0;
  for (const p of parts) size += p.length + 1;
  const out = new Uint8Array(Math.max(0, size - 1));
  let at = 0;
  parts.forEach((p, i) => {
    if (i > 0) out[at++] = 0x0a;
    out.set(p, at);
    at += p.length;
  });
  return out;
}

/**
 * Ten cua cac anh XObject khai bao trong /Resources cua trang.
 *
 * Dung de doi chieu: mot toan tu `/Ten Do` co the tro toi mot Form XObject (mot
 * trang con long nhau) chu khong phai anh. Cat mot Form XObject nham la xoa mat
 * mot mang noi dung, nen buoc doi chieu nay khong phai de lam sach - no la mot
 * cua chan.
 */
export function imageXObjectNames(doc: PDFDocument, pageIndex: number): Set<string> {
  const out = new Set<string>();
  const page = doc.getPages()[pageIndex];
  if (!page) return out;

  const resources = page.node.Resources();
  const xobjects = resources?.lookupMaybe(PDFName.of('XObject'), PDFDict);
  if (!xobjects) return out;

  for (const [key, value] of xobjects.entries()) {
    const stream = page.doc.context.lookup(value);
    if (!(stream instanceof PDFStream)) continue;
    const subtype = stream.dict.get(PDFName.of('Subtype'));
    if (subtype instanceof PDFName && subtype.asString() === '/Image') {
      out.add(key.asString().replace(/^\//, ''));
    }
  }
  return out;
}

/** Kich thuoc trang theo pdf-lib. Duong ghi phai do bang thuoc cua chinh no. */
export function pageSizeOf(
  doc: PDFDocument,
  pageIndex: number,
): { widthPt: number; heightPt: number; rotation: number } | null {
  const page = doc.getPages()[pageIndex];
  if (!page) return null;
  const size = page.getSize();
  const rotation = ((page.getRotation().angle % 360) + 360) % 360;
  return { widthPt: size.width, heightPt: size.height, rotation };
}
